// src/components/auth/Login.js

import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { ERROR_MESSAGES } from '../../constants';

const Login = () => {
  const [formData, setFormData] = useState({
    companyCode: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const { companyCode, username, password } = formData;

    if (!['trydx', 'demo'].includes(companyCode)) {
      setError('The company code you entered is not allowed. Please check and try again');
    } else {
      const result = await login(companyCode, username, password);
    
      if (!result.success) {
        setError(result.error || ERROR_MESSAGES.LOGIN_FAILED);
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            DelyvaX Integration Sandbox
          </h2>
        </div>
        <Card className="mt-8 p-6">
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label 
                  htmlFor="companyCode"
                  className="block text-sm font-medium text-gray-700"
                >
                  Company Code
                </Label>
                <div className="mt-1">
                  <Input
                    id="companyCode"
                    name="companyCode"
                    type="text"
                    required
                    disabled={isLoading}
                    placeholder="Enter company code"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.companyCode}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <Label 
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700"
                >
                  Username
                </Label>
                <div className="mt-1">
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    disabled={isLoading}
                    placeholder="Enter username"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <Label 
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </Label>
                <div className="mt-1">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    disabled={isLoading}
                    placeholder="Enter password"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {error && (
                <Alert 
                  variant="destructive" 
                  className="border-2 border-red-500 bg-red-50 rounded-lg px-4 py-3 shadow-sm"
                >
                  <AlertDescription className="text-red-700 font-medium ml-2 text-sm inline-block">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;